import "./PictureGallery.scss"

import React, { FunctionComponent, useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"

import IconDownload from "assets/img/icon-download.svg?react"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"

import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"

import Button from "components/Button/Button"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import Tag from "components/Tag/Tag"
import { IImage } from "models/ImageCollection"
import PictureSlider from "components/PictureSlider/PictureSlider"
import ThumbnailsSlider from "components/ThumbnailsSlider/ThumbnailsSlider"
import { downloadUrlAsFile } from "utils/utils"
import { IApiLink } from "@/types"
import { Dayjs } from "utils/dayjs"

export interface IPictureGalleryItem {
    index: number
    image: IImage
    date?: Dayjs
    tags?: string[]
}

export interface IPictureGalleryProps {
    items: IPictureGalleryItem[]
    activeIndex: number
    loadMore?(): Promise<void>
    onDelete?(link: IApiLink, itemIndex: number): Promise<void>
}

export const THUMBNAILS_MAX_COUNT = 11

const PictureGallery: FunctionComponent<React.PropsWithChildren<IPictureGalleryProps>> = ({
    items,
    activeIndex: activeIndexProp,
    loadMore,
    onDelete,
}) => {
    const { t } = useTranslation()
    const [activeIndex, setActiveIndex] = useState(activeIndexProp)
    const currentItem: IPictureGalleryItem | null = items[activeIndex] || items[0] || null
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const canSwitchToPrev = activeIndex > 0
    const canSwitchToNext = activeIndex < items.length - 1 && !isLoadingMore
    const thumbnailImages = items.map(item => item.image.thumbnailUri || "")

    useEffect(() => {
        setActiveIndex(activeIndexProp)
    }, [activeIndexProp])

    useEffect(() => {
        if (items.length === 0) return
        if (activeIndex > items.length - 1) {
            setActiveIndex(items.length - 1)
        }
    }, [activeIndex, items.length])

    const switchToPrev = useCallback(function() {
        if (!canSwitchToPrev) return
        setActiveIndex(activeIndex - 1)
    }, [activeIndex, canSwitchToPrev])

    const switchToNext = useCallback(async function() {
        if (!canSwitchToNext) return
        setActiveIndex(activeIndex + 1)
    }, [activeIndex, canSwitchToNext])

    function switchToIndex(index: number) {
        if (index < 0 || index > items.length - 1 || index === activeIndex) return
        setActiveIndex(index)
    }

    function handleDownloadClick() {
        if (!currentItem?.image.imageUri) return
        downloadUrlAsFile(currentItem.image.imageUri)
    }

    async function handleDeleteConfirm() {
        if (!onDelete || !currentItem?.image.deleteLink) return

        setIsDeleting(true)
        try {
            await onDelete(currentItem.image.deleteLink, activeIndex)
            setShowDeleteConfirm(false)
        } finally {
            setIsDeleting(false)
        }
    }

    const handleKeydown = useCallback((e: KeyboardEvent) => {
        if (e.key === "ArrowLeft") {
            switchToPrev()
        }
        if (e.key === "ArrowRight") {
            switchToNext()
        }
    }, [switchToNext, switchToPrev])

    useEffect(() => {
        document.addEventListener("keydown", handleKeydown)
        return () => document.removeEventListener("keydown", handleKeydown)
    }, [handleKeydown])

    async function handleLoadMore() {
        if (typeof loadMore !== "function") return
        setIsLoadingMore(true)
        await loadMore()
        setIsLoadingMore(false)
    }

    return (
        <div className="picture-gallery">
            {!!currentItem &&
                <div className="picture-gallery__slider">
                    <PictureSlider
                        imageUrl={currentItem.image.imageUri}
                        hasPrev={canSwitchToPrev}
                        hasNext={canSwitchToNext}
                        onPrev={switchToPrev}
                        onNext={switchToNext}
                        additionalControls={
                            <div className="picture-gallery__controls">
                                <Button
                                    round
                                    theme="grey"
                                    className="picture-gallery__download"
                                    icon={<IconDownload />}
                                    onClick={handleDownloadClick}>
                                </Button>
                                {!!onDelete && !!currentItem.image.deleteLink &&
                                    <Button
                                        round
                                        theme="grey"
                                        className="picture-gallery__delete"
                                        icon={<IconTrashBin />}
                                        ariaLabel={t("common.delete")}
                                        disabled={isDeleting}
                                        loading={isDeleting}
                                        onClick={() => setShowDeleteConfirm(true)}>
                                    </Button>
                                }
                            </div>
                        }
                    />
                </div>
            }
            {thumbnailImages.length > 0 &&
                <div className="mt-4">
                    <ThumbnailsSlider
                        maxCountToShow={THUMBNAILS_MAX_COUNT}
                        images={thumbnailImages}
                        activeIndex={activeIndex}
                        onSwitchToIndex={switchToIndex}
                        isLoadingMore={isLoadingMore}
                        loadMore={loadMore ? handleLoadMore : undefined}
                    />
                </div>
            }
            {!!currentItem?.date &&
                <Text className="mt-3 text-center">{currentItem.date.format(t('config.dateLongTimeFormat'))}</Text>
            }
            {!!currentItem?.tags && currentItem.tags.length > 0 &&
                <Flex
                    className="mt-3"
                    alignItems="center"
                    justifyContent="center"
                    wrap="wrap">
                    {currentItem.tags.map((tag, idx) => (
                        <FlexItem key={idx} className="mb-1 mr-1 max-w-100">
                            <Tag label={tag} />
                        </FlexItem>
                    ))}
                </Flex>
            }
            {showDeleteConfirm &&
                <ConfirmModal
                    title={t('common.warning')}
                    maxWidth="586px"
                    theme="danger"
                    confirmButtonLabel={t('common.delete')}
                    isBusy={isDeleting}
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleDeleteConfirm}>
                    {t('documents.deletePicutreConfirm')}
                </ConfirmModal>
            }
        </div>
    )
}

export default PictureGallery
